import type {
  CardFailureReason,
  CardPaymentFailure,
  PostStripeBillingRetryCharge,
} from "@/server-types";
import { addToast } from "@heroui/react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";

export function describeCardFailureReason(reason: CardFailureReason): string {
  switch (reason) {
    case "insufficientFunds":
      return t`Na kartici ni dovolj sredstev.`;
    case "expiredCard":
      return t`Kartica je potekla.`;
    case "needsAuthentication":
      return t`Banka zahteva ponovno potrditev kartice — dodajte jo znova.`;
    case "cardBlocked":
      return t`Banka je kartico zavrnila — dodajte drugo kartico.`;
    default:
      return t`Banka je plačilo zavrnila.`;
  }
}

/** The earliest moment any of the failures may be retried on the same card, or null when none can. */
export function earliestRetryAt(failures: CardPaymentFailure[]): Date | null {
  const times = failures
    .map((failure) => failure.retryAvailableAt)
    .filter((at): at is Date => at != null)
    .map((at) => new Date(at).getTime());

  return times.length > 0 ? new Date(Math.min(...times)) : null;
}

/** The latest point at which the card rail gives up on any of the failures. */
export function latestCollectionEnd(
  failures: CardPaymentFailure[],
): Date | null {
  const times = failures
    .map((failure) => failure.collectionEndsAt)
    .filter((at): at is Date => at != null)
    .map((at) => new Date(at).getTime());

  return times.length > 0 ? new Date(Math.max(...times)) : null;
}

export function formatFailureDate(date: Date | string): string {
  return dayjs(date).locale(i18n.locale).format("D. MMM YYYY");
}

export function formatFailureTime(date: Date | string): string {
  return dayjs(date).locale(i18n.locale).format("D. MMM, HH:mm");
}

export function notifyRetryResult(
  result: PostStripeBillingRetryCharge["response"],
) {
  if (result.charged > 0 && result.declined === 0) {
    addToast({ title: t`Plačilo je uspelo.`, color: "success" });
    return;
  }

  if (result.declined > 0) {
    addToast({ title: t`Plačilo ponovno ni uspelo.`, color: "danger" });
    return;
  }

  addToast({
    title: t`Plačila zdaj ni mogoče ponoviti. Poskusite pozneje.`,
    color: "warning",
  });
}
